import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in ms

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null);
  const timerRef = useRef(null);
  const tokenRef = useRef(token);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const realUser = authUser;
  const user = (viewAsRole && authUser?.role === 'admin')
    ? { ...authUser, role: viewAsRole }
    : authUser;

  // Start/stop inactivity tracking based on login state
  useEffect(() => {
    if (!token) {
      clearTimeout(timerRef.current);
      return;
    }

    const reset = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (!tokenRef.current) return;
        setToken(null);
        setAuthUser(null);
        setViewAsRole(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, reset));
    reset();

    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      clearTimeout(timerRef.current);
    };
  }, [token]);

  const logout = () => {
    setToken(null);
    setAuthUser(null);
    setViewAsRole(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearTimeout(timerRef.current);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setAuthUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (name, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { name, password });
      const { token, role, name: userName } = response.data;
      const userData = { name: userName, role };
      setToken(token);
      setAuthUser(userData);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { success: true, role };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (name, email, password, role = 'user', authToken = null) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : (token ? { Authorization: `Bearer ${token}` } : {});
      await axios.post(`${API}/auth/register`, { name, email, password, role }, { headers });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, realUser, token, login, register, logout, loading, getAuthHeader, viewAsRole, setViewAsRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
